<template>
  <section class="block">
    <h1 v-if="project.name !== ''">
      プロファイル: <span>{{ project.name }}</span>
    </h1>
    <h1 v-else>プロファイル</h1>
    <Snackbar :option="snackbar"></Snackbar>
    <div class="form">
      <table>
        <tbody>
        <tr>
          <th>プロファイル名</th>
          <td><input type="text" class="field" v-model="project.name" /></td>
        </tr>
        <tr>
          <th>説明</th>
          <td>
            <input type="text" class="field" v-model="project.description" />
          </td>
        </tr>
        <tr v-if="edit && this.qrUri">
          <th>QRコード表示用URL</th>
          <td class="qrlink">
            <a :href="this.qrUri" target="_blank">{{ this.qrUri }}</a>
            <font-awesome-icon icon="copy" @click="copyQrUri" />
          </td>
        </tr>
        <tr>
          <th>アイコン</th>
          <td>
            <input type="file" hidden accept="image/aces" ref="pdfInput"
                   @change="handleInputChange($event)" />
            <div class="dropzone" @drop="handleDrop" @dragover="handleDragover">
              <p v-if="!project.icon || project.icon === ''">
                <span>Drop an image file here!</span>
              </p>
              <p v-else>
                <img :src="iconUrl" />
                <font-awesome-icon icon="minus-circle" @click="resetIcon" />
              </p>
            </div>
          </td>
        </tr>
        <tr v-if="edit && project.created">
          <th>作成日時</th>
          <td>{{ new Date(project.created).toLocaleString() }}</td>
        </tr>
        <tr v-if="edit && project.updated">
          <th>更新日時</th>
          <td>{{ new Date(project.updated).toLocaleString() }}</td>
        </tr>
        </tbody>
      </table>
      <nav>
        <button class="btn btn-secondary" v-if="!edit" @click="add">追加</button>
        <button class="btn btn-secondary" v-if="edit" @click="update">更新</button>
        <button class="btn btn-danger" v-if="edit" @click="remove">削除</button>
      </nav>
    </div>
  </section>
  <Dialog ref="remove" class="dialog">
    <h4 class="title">Confirm</h4>
    <div class="message">
      <p class="text">Do you really remove this profile?</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="removeCancel">Cancel</button>
      <button class="btn btn-danger" @click="removeConfirmed">削除</button>
    </div>
  </Dialog>
  <Dialog ref="error" class="dialog">
    <h4 class="title">Error</h4>
    <div class="message">
      <p class="text">{{ error.message }}</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="errorClose">Close</button>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Dialog from '@/components/Dialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { Project, ProjectData } from '@/lib/project';
import router from '@/router';

export default defineComponent({
  name: 'ProjectEditor',
  components: {
    Dialog,
    Snackbar,
  },
  props: {
    edit: Boolean,
    prjSlug: {
      type: String,
      require: true,
    },
    orgSlug: {
      type: String,
      require: true,
    },
  },
  data() {
    return {
      project: {
        name: '',
        slug: '',
        description: '',
        icon: '',
        created: undefined,
        updated: undefined,
      } as ProjectData,
      message: '',
      iconMimeType: '',
      qrUri: '',
      snackbar: {
        type: '',
        message: '',
      },
      error: {
        message: '',
        back: false,
      },
    };
  },
  computed: {
    iconUrl(): string | undefined {
      if (this.project.icon && this.project.icon !== '') {
        return `data:image/png;base64,${this.project.icon}`;
      } else {
        return undefined;
      }
    },
  },
  async mounted() {
    if (!this.orgSlug) {
      this.error.message = '組織が指定されていません！';
      this.error.back = true;
      (this.$refs.error as InstanceType<typeof Dialog>)?.open();
      return;
    }
    if (this.prjSlug) {
      try {
        const prj = new Project(this.orgSlug, this.prjSlug);
        const data = await prj.get();
        if (data) {
          this.project = data;
          this.qrUri = `${process.env.VUE_APP_PROFILE_ROOT}/${this.prjSlug}`;
        } else {
          this.error.message = '組織が指定されていません！';
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    }
  },
  methods: {
    openFileDialog() {
      (this.$refs.pdfInput as HTMLInputElement).click();
    },
    validate(): boolean {
      if (this.project.name === '') {
        this.snackbar.type = 'warning';
        this.snackbar.message = 'プロフィール名を入力してください！';
        return false;
      } else if (this.project.description === '') {
        this.snackbar.type = 'warning';
        this.snackbar.message = 'プロフィールの説明を入力してください！';
        return false;
      } else {
        this.snackbar.message = '';
        return true;
      }
    },
    async add() {
      if (!this.validate() || !this.orgSlug) {
        return;
      }
      try {
        const prj = new Project(this.orgSlug);
        const slug = await prj.add(this.project as ProjectData);
        if (slug) {
          console.info('Profile added!');
          router.replace(`/organizations/${this.orgSlug}/projects/${slug}`);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'プロフィールの作成に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    async update() {
      if (!this.validate() || !this.orgSlug) {
        return;
      }
      try {
        const prj = new Project(this.orgSlug, this.prjSlug);
        const slug = await prj.update(this.project as ProjectData);
        if (slug) {
          this.snackbar.type = 'success';
          this.snackbar.message = 'プロフィールが更新されました！';
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'このプロフィールを更新できませんでした！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    remove() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.open();
    },
    async removeConfirmed() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
      if (!this.orgSlug) {
        return;
      }
      try {
        const prj = new Project(this.orgSlug, this.prjSlug);
        if (await prj.delete()) {
          router.replace(`/organizations/${this.orgSlug}/projects`);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'このプロファイルを削除できません。サポートにお問い合わせください。';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    removeCancel() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
    },
    handleInputClick(event: Event) {
      console.log('handleInputClick', event);
      if (this.iconUrl) {
        event.preventDefault();
      }
    },
    async handleInputChange(event: Event) {
      event.preventDefault();

      const target = event.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) return;

      const file = target.files[0];
      if (file.type.toLowerCase() !== 'image/png') {
        this.snackbar.type = 'warning';
        this.snackbar.message = ' アイコンファイルは PNG ファイルでなければなりません！';
        return;
      }
      this.iconMimeType = file.type;
      this.project.icon = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          const iconUrl = reader.result as string;
          resolve(iconUrl.split(',')[1]);
        });
        reader.readAsDataURL(file);
      });
    },
    async handleDrop(event: DragEvent) {
      event.preventDefault();

      if (!event.dataTransfer) return;

      const item = event.dataTransfer.items[0];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (!file) return;
        if (file.type.toLowerCase() !== 'image/png') {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'アイコンファイルは PNG 形式のみ対応しています！';
          return;
        }
        this.iconMimeType = file.type;
        this.project.icon = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            const iconUrl = reader.result as string;
            resolve(iconUrl.split(',')[1]);
          });
          reader.readAsDataURL(file);
        });
      }
    },
    async handleDragover(event: Event) {
      event.preventDefault();
    },
    copyQrUri() {
      const el = document.createElement('textarea');
      el.value = this.qrUri;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      const selection = document.getSelection();
      if (!selection) return;
      const selected = selection.rangeCount > 0 ? selection.getRangeAt(0) : false;
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      if (selected) {
        selection.removeAllRanges();
        selection.addRange(selected);
      }
      alert('Copied!');
    },
    resetIcon() {
      this.project.icon = '';
    },
    errorClose() {
      (this.$refs.error as InstanceType<typeof Dialog>)?.close();
      this.error.back && this.$router.back();
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;
@use '@/assets/dialog.scss' as *;

.block {
  .form {
    margin: 0 10px;
    @media (min-width: $tablet) {
      margin: 0 20px;
    }
    @media (min-width: $desktop) {
      margin: 0 40px;
    }

    table {
      width: 100%;
      max-width: 640px;
    }

    th {
      padding: 5px;
      font-weight: normal;
    }

    td {
      padding: 5px;
    }

    .field {
      width: 100%;
    }

    .qrlink {
      word-break: break-all;

      a {
        text-decoration: none;
      }

      svg {
        cursor: pointer;
        margin-left: 10px;
      }
    }

    .dropzone {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 180px;
      padding: 5px;
      border: 1px solid var(--bs-gray-400);
      border-radius: 5px;
      cursor: pointer;

      p {
        position: relative;
        margin: 0;
        color: var(--bs-gray-600);

        img {
          max-width: 140px;
          max-height: 140px;
        }

        svg {
          cursor: pointer;
          position: absolute;
          top: -0.5rem;
          right: -0.5rem;
          font-size: 1.8rem;
          color: var(--bs-danger);
        }
      }
    }

    nav {
      display: flex;
      margin-top: 20px;

      button {
        margin-right: 20px;

        &:last-child:not(:first-child) {
          margin-left: auto;
          margin-right: 0;
        }
      }
    }
  }
}

.dialog {
  @include dialog();
}
</style>